import React, { useState, useEffect, useContext } from "react";
import {BadgeInfo, ArrowBigRight, Compass, SkipForward} from 'lucide-react';
import he from 'he';
import styles from "./Questions.module.scss";
import { DEBUG, PATH } from '../../utils/constants';

import useSafeAsyncEffect from '../../utils/hooks/useSafeAsyncEffect';
import MarkdownRenderer from '../../utils/MarkdownRenderer/MarkdownRenderer';

// import { reportClientError } from '../../utils/api/reportAPI';
// import { reportClientError } from '../../utils/api/reportClientErrorAPI';
import { sendScore } from '../../utils/api/scoresAPI';

import { LanguageContext } from '../../context/LanguageContext';
import translations from '../../utils/translations/QuestionTranslations';

const path = PATH;
const debug = DEBUG;


const Questions = ({parcoursId, questions, repLibres, repQcm, onFinishGame, setQuestionAchieved, startQuestionTimer, saveQuestionsToStorage, updateQuestionField}) => {

  // Utilisation du contexte de langue global et accès simplifié aux traductions
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.fr;

// --------------------- STATES --------------------------

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const [userAnswers, setUserAnswers] = useState({});
  const [feedback, setFeedback] = useState("");
  const [feedbacktext, setFeedbackText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showHelperModal, setShowHelperModal] = useState(false);
  

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;


// --------------------- EFFECTS --------------------------

  useEffect(() => {
    if (currentQuestion && !currentQuestion.startTime) {
      startQuestionTimer(currentQuestion.id);
      saveQuestionsToStorage();
      if (debug) console.log("⏱️ Timer initialisé pour la question ID", currentQuestion.id);
    }
  }, [currentQuestion]);


  useEffect(() => {
    if (!questions.length || isModalOpen) return;
  
    const nextIndex = findNextUnachievedIndex(currentQuestionIndex, questions);
  
    if (nextIndex === -1) {
      if (debug) console.log("🎯 Toutes les questions sont terminées !");
      onFinishGame();
    } else if (nextIndex !== currentQuestionIndex) {
      if (debug) console.log(`➡️ Passage automatique de la question ${currentQuestionIndex} à ${nextIndex}`);
      setCurrentQuestionIndex(nextIndex);
    }
  }, [currentQuestionIndex, questions]);
  

  // Pour afficher le temps écoulé sur une question 
  useSafeAsyncEffect(async (isMounted) => {
    const interval = setInterval(() => {
      if (isMounted.current && currentQuestion?.startTime) {
        const now = new Date();
        const start = new Date(currentQuestion.startTime);
        const diff = Math.floor((now - start) / 1000);
  
        const days = Math.floor(diff / (60 * 60 * 24));
        const hours = Math.floor((diff % (60 * 60 * 24)) / (60 * 60));
        const minutes = Math.floor((diff % (60 * 60)) / 60);
        const seconds = diff % 60;
  
        setElapsedTime({ days, hours, minutes, seconds });
      }
    }, 1000);
  
    return () => clearInterval(interval); // Nettoie le timer
  }, [currentQuestion?.startTime], { debug: debug });


  const findNextUnachievedIndex = (startIndex, questions) => {
    for (let i = startIndex; i < questions.length; i++) {
      if (!questions[i]?.achieved) {
        return i;
      }
    }
    return -1; // Plus rien à jouer
  };
  
  //------------------------- EVENTS -----------------------------

  const handleAnswerChange = (event) => {
    setUserAnswers({
      ...userAnswers,
      [currentQuestion.id]: event.target.value,
    });
  };

  // Gestionnaire de soumission du formulaire pour déclencher la validation avec la touche Entrée
  const handleSubmit = (e) => {
    e.preventDefault();
    validateAnswer();
  };

  const handleFail = () => {
    setIsModalOpen(false);
    setFeedback(false);
    setFeedbackText("");

    if (currentQuestion.nbCompass === 0) {
      return; 
    }

    const newCompassCount = Math.max(currentQuestion.nbCompass - 1, 0);
    updateQuestionField(currentQuestion.id, 'nbCompass', newCompassCount);
    saveQuestionsToStorage();
  };

//------------------------- FUNCTIONS -----------------------------

  function normalize(str) {
    return he.decode(str || "")             // 🔥 Décodage des entités HTML
      .normalize("NFD")                     // Supprime les accents
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ");                // supprime les espaces multiples
  }

  const validateAnswer = async () => {
    const rawUserAnswer = userAnswers[currentQuestion.id] || "";
    const userAnswer = normalize(rawUserAnswer);

    let isCorrect = false;

    if (currentQuestion.type === "libre") {

      const entry = repLibres.find(
        (rep) => String(rep.idQuestion) === String(currentQuestion.id)
      );
      const rawAnswers = entry?.possibilities;

      let validAnswers = [];
      let parseError = null;
      try {
        validAnswers = typeof rawAnswers === "string" ? JSON.parse(rawAnswers) : rawAnswers;
      } catch (e) {
        console.error("Échec du parse JSON des réponses libres :", e);
        parseError = e;
      }

      const normalizedExpected = validAnswers?.map(ans => normalize(ans));
      isCorrect = normalizedExpected?.some(ans => ans === userAnswer);

      if (!isCorrect) {
        if (debug) console.log("Erreur de validation !");
      }

    } else if (currentQuestion.type === "qcm") {
      
      const selectedAnswer = parseInt(userAnswer);
      isCorrect = repQcm.some((reponse) => parseInt(reponse.id) === selectedAnswer && parseInt(reponse.isTrue) === 1);

    }

    if(isCorrect) {
      const now = new Date();
      const start = new Date(currentQuestion.startTime);
      const diff = Math.floor((now - start) / 1000); // Calcul du temps passé sur la question

      await setQuestionAchieved(currentQuestion.id, {
        duration: diff,
        nbCompass: currentQuestion.nbCompass,
        aborted: false,
      });
    }
  
    setFeedback(isCorrect);
    setFeedbackText(isCorrect ? t.correctAnswer : t.tryAgain);
    setIsModalOpen(true); 
  };

  const nextQuestion = async () => {
    setIsModalOpen(false);
    setFeedback(false);
    setFeedbackText("");
    
    if (debug) console.log("État question actuelle : ", currentQuestion);

    await saveCurrentScoreToLocal(); //New

    if (isLastQuestion && currentQuestion.achieved) {
      // En théorie on ne passe plus par là car ce cas est géré directement dans le useEffect...
      onFinishGame(); 
      return;
    } 

    // Remise à zéro de l'affichage local
    setElapsedTime({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  };

  const interruptCurrentQuestion = async () => {
    if (!currentQuestion || currentQuestion.achieved) return;

    const now = new Date();
    const start = new Date(currentQuestion.startTime);
    const diff = Math.floor((now - start) / 1000);

    await setQuestionAchieved(currentQuestion.id, {
      duration: diff,
      nbCompass: currentQuestion.nbCompass,
      aborted: true,
    });

    saveQuestionsToStorage();
  };

  //New
  const saveCurrentScoreToLocal = async () => {
    const now = new Date(); // Réinitialise la date et l'heure du début de la question (suivante)
    if (currentQuestion?.startTime) { // on vérifie si la question actuelle a bien un timer qui a été lancé
      const start = new Date(currentQuestion.startTime); // on crée un objet Date à partir de ce timer
      const diff = Math.floor((now - start) / 1000); // On calcule le temps en secondes écoulé depuis le début de la question
      // await setQuestionAchieved(currentQuestion.id, { duration: diff, nbCompass: currentQuestion.nbCompass, aborted: false }); // On met à jour la question que l'on vient de terminer dans le tableau local
      await scoreManagement(localStorage.getItem("userId"), currentQuestion.id, diff, currentQuestion.nbCompass, false, localStorage.getItem(`progression_${parcoursId}`)); // On enregistre le score via l'api

      // Sauvegarder l'état final (initialement que dans handleFinishGame, à voir si supporté correctement !)
      saveQuestionsToStorage();
    }
  };

  async function scoreManagement(idUser, idQuestion, time, compass, aborted, idProgression) {
    const score = { idUser, idQuestion, time, compass, aborted, idProgression };

    const success = await sendScore(score);
    if (success && !success.message) {
        console.log("Score envoyé !");
    } else {
        console.log("Score sauvegardé localement, en attente de connexion...");
    }
  }

  // A vérifier mais je pense que je n'y passe plus...
  // const handleFinishGame = async () => {
  //   const now = new Date();
  
  //   if (currentQuestion?.startTime) {
  //     const start = new Date(currentQuestion.startTime);
  //     const diff = Math.floor((now - start) / 1000);
  
  //     // Sauvegarder la question finale
  //     await setQuestionAchieved(currentQuestion.id, {
  //       duration: diff,
  //       nbCompass: currentQuestion.nbCompass,
  //     });
  
  //     // Envoyer score
  //     // await scoreManagement(localStorage.getItem("userId"), currentQuestion.id, diff, currentQuestion.nbCompass, false);
  
  //     // Sauvegarder l'état final
  //     saveQuestionsToStorage();
  //   }
  
  //   // Fin du parcours
  //   onFinishGame();
  // };


  if (!currentQuestion) {
    console.warn("Aucune question en cours, affichage bloqué pour éviter une erreur");
    return <div>Chargement...</div>;
  }

  return (
    <div className={styles.container}>
      <p className={styles.questionRank}>{t.questionLabel} {currentQuestionIndex + 1}/{questions.length}</p>

      {/* <p>
        Temps écoulé : 
        {elapsedTime.days > 0 && ` ${elapsedTime.days} jour${elapsedTime.days > 1 ? 's' : ''},`}
        {elapsedTime.hours > 0 && ` ${elapsedTime.hours} heure${elapsedTime.hours > 1 ? 's' : ''},`}
        {elapsedTime.minutes > 0 && ` ${elapsedTime.minutes} minute${elapsedTime.minutes > 1 ? 's' : ''},`}
        {elapsedTime.seconds} seconde{elapsedTime.seconds > 1 ? 's' : ''}
      </p> */}
      <div className={styles.compassContainer}>
          {Array.from({ length: currentQuestion.nbCompass }).map((_, i) => (
              <span key={i}>🧭</span>
          ))}
      </div>

      <div className={styles.questionText}><MarkdownRenderer content={currentQuestion.libelle} /></div>
      {currentQuestion.img && <img src={`${path}${currentQuestion.img}`} alt="Illustration" className={styles.questionImage} />}
      
      <form onSubmit={handleSubmit}>
        <div className={styles.answerContainer}>
          {currentQuestion.type === "libre" ? (
            <input
              type="text"
              placeholder={t.yourAnswer}
              className={styles.answerInput}
              value={userAnswers[currentQuestion.id] || ""}
              onChange={handleAnswerChange}
            />
          ) : (
            <div className={styles.qcmOptions}>
              {repQcm
                .filter((rep) => rep.idQuestion === currentQuestion.id)
                .map((option) => (
                  <label key={option.id} className={styles.option}>
                    <input
                      type="radio"
                      name={`qcm-${currentQuestion.id}`}
                      value={option.id}
                      onChange={handleAnswerChange}
                      className={styles.hiddenRadio} // Caché
                    />
                    <div className={styles.optionCard}> 
                      {option.img ? (
                        <img src={`${path}${option.img}`} alt="Option visuelle" className={styles.optionImage} />
                      ) : (
                        <div className={styles.optionText}><MarkdownRenderer content={option.txt} /></div>
                      )}
                    </div>
                  </label>
                ))}
            </div>
          )}
        </div>

        <div className={styles.bottomContainer}>
          <div className={styles.buttonsContainer}>
            <div className={styles.buttons}>
              <button type="submit" className={styles.validateButton}>
                {t.validateAnswer} <ArrowBigRight size={30} />
              </button>
              { currentQuestion.nbCompass <= 0 && (
                <button type="button" onClick={interruptCurrentQuestion} className={styles.rightButton}>
                    <span>Passer la question</span> <SkipForward />
                </button>
              )}
            </div>
            
            <div className={styles.iconContainer}>
              <BadgeInfo 
                className={`${styles.icon} ${currentQuestion.nbCompass < 3 ? styles.activeHint : ''}`} 
                size={36} 
                onClick={() => currentQuestion.nbCompass < 3 && setShowHelperModal(true)}
              />
            </div>
          </div>
        </div>
      </form>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <p className={styles.modalText}>{feedbacktext}</p>
            
            {/* Cas où c'est la dernière question et la réponse est correcte */}
            {/* {isLastQuestion && feedback && currentQuestion.achieved === true ? (
              <>
                <div><img src="/assets/img/sirene1.png" alt="Illustration" className={styles.image} /></div>
                <button onClick={handleFinishGame} className={styles.nextButton}>
                  {t.finishGame}
                </button>
              </>
            ) : (
              <> */}
                {/* Cas normal : passage à la question suivante après une bonne réponse */}
                {feedback ? (
                  <>
                    <div><img src="/assets/img/sirene1opt.gif" alt="Illustration" className={styles.image} /></div>
                    <button onClick={nextQuestion} className={styles.nextButton}>
                      {t.nextQuestion}
                    </button>
                  </>
                ) : (
                  /* Cas d'une mauvaise réponse : réessayer */
                  <>
                    <div><img src="/assets/img/sirene2opt.gif" alt="Illustration" className={styles.image} /></div>
                    <button onClick={handleFail} className={styles.retryButton}>
                      {t.retry}
                    </button>
                  </>
                )}
              {/* </>
            )} */}
          </div>
        </div>
      )}

      {showHelperModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            {currentQuestion.helper && (
              <div className={styles.modalText}><MarkdownRenderer content={currentQuestion.helper} /></div>
            )}
            {currentQuestion.img_helper && (
              <img 
                src={`${path}${currentQuestion.img_helper}`} 
                alt="Indice visuel"
                className={styles.hintImage}
              />
            )}
            {!currentQuestion.helper && !currentQuestion.img_helper && (
              <p className={styles.modalText}>{t.noHintAvailable}</p>
            )}

            <button onClick={() => setShowHelperModal(false)} className={styles.retryButton}>
              {t.closeButton}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default Questions;
